import { Button } from "@bowfast/ui/components/button";
import { Card, CardContent, CardFooter, CardHeader } from "@bowfast/ui/components/card";
import { cn } from "@bowfast/ui/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronRightIcon, PenLineIcon, PlusIcon, Trash2Icon } from "lucide-react";
import { useState } from "react";
import { EASE_OUT } from "~/lib/animations";
import type { Skill, SkillCategory } from "~/pages/skills/skills.types";
import { SkillRow } from "./SkillRow";

interface CategoryCardProps {
  category: SkillCategory;
  onAddSkill: (categoryId: string) => void;
  onEditCategory: (c: SkillCategory) => void;
  onDeleteCategory: (id: string) => void;
  onEditSkill: (s: Skill) => void;
  onDeleteSkill: (id: string) => void;
}

export function CategoryCard({
  category,
  onAddSkill,
  onEditCategory,
  onDeleteCategory,
  onEditSkill,
  onDeleteSkill,
}: Readonly<CategoryCardProps>) {
  const [expanded, setExpanded] = useState(true);

  return (
    <Card>
      <CardHeader className="border-b border-border pb-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="flex items-center gap-2 text-left"
            aria-expanded={expanded}
          >
            <div className={cn("transition-transform duration-200", expanded ? "rotate-90" : "rotate-0")}>
              <ChevronRightIcon className="size-4 text-muted-foreground" />
            </div>
            <div className="min-w-0">
              <p className="font-heading text-[0.9375rem] font-semibold leading-tight">{category.name}</p>
              {category.description && <p className="mt-0.5 text-xs text-muted-foreground">{category.description}</p>}
            </div>
          </button>

          <span className="ml-1 rounded-full bg-muted px-2 py-0.5 text-[0.625rem] font-bold text-muted-foreground">
            {category.skills.length}
          </span>

          <div className="ml-auto flex shrink-0 items-center gap-1.5">
            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => onAddSkill(category.id)}>
              <PlusIcon className="size-3.5" />
              Add skill
            </Button>
            <Button variant="ghost" size="icon-sm" onClick={() => onEditCategory(category)}>
              <PenLineIcon className="size-3.5" />
              <span className="sr-only">Edit category</span>
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              className="text-coral-500 hover:text-coral-700"
              onClick={() => onDeleteCategory(category.id)}
            >
              <Trash2Icon className="size-3.5" />
              <span className="sr-only">Delete category</span>
            </Button>
          </div>
        </div>
      </CardHeader>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="skills"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: EASE_OUT }}
            style={{ overflow: "hidden" }}
          >
            <CardContent className="p-0">
              {category.skills.length === 0 ? (
                <div className="py-8 text-center text-sm text-muted-foreground">No skills in this category yet.</div>
              ) : (
                category.skills.map((skill) => (
                  <SkillRow key={skill.id} skill={skill} onEdit={onEditSkill} onDelete={onDeleteSkill} />
                ))
              )}
            </CardContent>
            {category.skills.length > 0 && (
              <CardFooter className="justify-end px-4 py-3">
                <Button
                  size="sm"
                  variant="ghost"
                  className="gap-1.5 text-muted-foreground"
                  onClick={() => onAddSkill(category.id)}
                >
                  <PlusIcon className="size-3.5" />
                  Add skill to {category.name}
                </Button>
              </CardFooter>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}
